import { SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatNumber } from '@/shared/lib/format';

import type { ReportFilterChanges } from '../lib/report-filters';
import { GROUPING_LABEL } from '../lib/report-labels';
import type { FinancialReport, FinancialRow } from '../types/report.types';
import { GroupLabel } from './filter-value';

type MoneyField =
  | 'collected'
  | 'discounts'
  | 'channelFees'
  | 'netRevenue'
  | 'actualCost'
  | 'grossProfit'
  | 'mgmtFee'
  | 'netProfit'
  | 'partnerShare'
  | 'retained'
  | 'refundsAmount';

type ColumnId = MoneyField | 'realizedMarginPct';

const COLUMNS: readonly { id: ColumnId; label: string }[] = [
  { id: 'collected', label: 'Collected' },
  { id: 'discounts', label: 'Discounts' },
  { id: 'channelFees', label: 'Channel fees' },
  { id: 'netRevenue', label: 'Net revenue' },
  { id: 'actualCost', label: 'Cost' },
  { id: 'grossProfit', label: 'Gross profit' },
  { id: 'mgmtFee', label: 'Mgmt fee' },
  { id: 'netProfit', label: 'Net profit' },
  { id: 'partnerShare', label: 'Partner share' },
  { id: 'retained', label: 'Retained' },
  { id: 'refundsAmount', label: 'Refunds' },
  { id: 'realizedMarginPct', label: 'Margin' },
];

/** A readable default (partner share included: what we owe is always in view); the rest is one click away in "Columns". */
const DEFAULT_VISIBLE: ReadonlySet<ColumnId> = new Set<ColumnId>([
  'collected',
  'channelFees',
  'netRevenue',
  'actualCost',
  'grossProfit',
  'netProfit',
  'partnerShare',
  'retained',
  'realizedMarginPct',
]);

/** Columns that only mean something when a management fee or partner share applies. */
const PARTNER_COLUMNS: readonly ColumnId[] = ['mgmtFee', 'netProfit', 'partnerShare', 'retained'];

/**
 * The default columns for this report. Where no order carried a management fee
 * or partner share (both 0% in the pricing parameters), those columns would
 * only repeat gross profit or show zeros, so they start hidden — still one
 * click away in "Columns".
 */
function defaultColumns(report: FinancialReport): ReadonlySet<ColumnId> {
  const shared = report.totals.some((total) => total.partnerShare !== 0 || total.mgmtFee !== 0);
  if (shared) return DEFAULT_VISIBLE;
  return new Set([...DEFAULT_VISIBLE].filter((id) => !PARTNER_COLUMNS.includes(id)));
}

function cellValue(row: FinancialRow, id: ColumnId): string {
  if (id === 'realizedMarginPct')
    return row.realizedMarginPct === null ? '—' : `${row.realizedMarginPct}%`;
  return formatCurrency(row[id], row.currencyCode ?? 'USD');
}

/**
 * The financial report by group, one row per group and currency, with the
 * period totals per currency in the footer. Columns can be shown or hidden;
 * which ones is a view preference, so it stays in component state.
 */
export function FinancialTable({
  report,
  onFilter,
}: {
  report: FinancialReport;
  /** Applies the filter a row's channel, provider, brand or denomination label stands for. */
  onFilter?: (changes: ReportFilterChanges) => void;
}) {
  const [visible, setVisible] = useState<ReadonlySet<ColumnId>>(() => defaultColumns(report));
  const columns = COLUMNS.filter((column) => visible.has(column.id));
  const grouping = GROUPING_LABEL[report.groupBy];

  const toggle = (id: ColumnId, show: boolean) =>
    setVisible((current) => {
      const next = new Set(current);
      if (show) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Money by {grouping.toLowerCase()}</CardTitle>
        <span data-print="hide">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                <SlidersHorizontal className="size-3.5" aria-hidden />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-xs">Visible columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={visible.has(column.id)}
                  onCheckedChange={(value) => toggle(column.id, value === true)}
                  onSelect={(event) => event.preventDefault()}
                  className="text-xs"
                >
                  {column.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="text-xs" aria-label={`Money by ${grouping.toLowerCase()}`}>
            <TableHeader>
              <TableRow>
                <TableHead>{grouping}</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead className="text-end">Orders</TableHead>
                {columns.map((column) => (
                  <TableHead key={column.id} className="text-end">
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={`${row.key}-${row.currencyCode ?? ''}`}>
                  <TableCell>
                    <GroupLabel
                      grouping={report.groupBy}
                      rowKey={row.key}
                      label={row.label}
                      onFilter={onFilter}
                    />
                  </TableCell>
                  <TableCell>{row.currencyCode ?? '—'}</TableCell>
                  <TableCell className="text-end tabular-nums">
                    {formatNumber(row.completedOrders)}
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.id} className="text-end tabular-nums">
                      {cellValue(row, column.id)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            {report.totals.length > 0 && (
              <TableFooter>
                {report.totals.map((total) => (
                  <TableRow key={total.currencyCode ?? 'none'}>
                    <TableCell>Total</TableCell>
                    <TableCell>{total.currencyCode ?? '—'}</TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatNumber(total.completedOrders)}
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell key={column.id} className="text-end tabular-nums">
                        {cellValue(total, column.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableFooter>
            )}
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
